<template>
  <div>
    <!-- TopZone -->
    <q-field borderless v-show="agencies.length > 1">
      <template v-slot:before>
        <q-icon name="store_mall_directory" color="primary" />
      </template>
      <template v-slot:control>
        <div class="self-center full-width no-outline" tabindex="0">
          <sw-filterable-select
            v-model="selectedAgency"
            :label="$t('common.agency')"
            :options="agencySelectOptions"
          />
        </div>
      </template>
    </q-field>
    <!-- Zone -->
    <q-field borderless v-show="selectedAgencyId">
      <template v-slot:before>
        <q-icon name="location_city" color="primary" />
      </template>
      <template v-slot:control>
        <div class="self-center full-width no-outline" tabindex="0">
          <sw-filterable-select
            v-model="selectedResidence"
            :label="$t('issueDetails.residence')"
            :options="residenceSelectOptions"
          />
        </div>
      </template>
    </q-field>
    <!-- Place -->
    <q-field borderless v-show="selectedResidenceId">
      <template v-slot:before>
        <q-icon name="place" color="primary" />
      </template>
      <template v-slot:control>
        <div class="self-center full-width no-outline" tabindex="0">
          <sw-filterable-select
            v-model="selectedPlace"
            :label="$t('issueDetails.place')"
            :options="placeSelectOptions"
            :clearable="showClearSelectedPlace"
            @clearSelected="resetSelectedId"
          />
        </div>
      </template>
    </q-field>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SwFilterableSelect from 'src/components/inputs/sw-filterable-select.vue'
import { alphabeticalSort } from 'src/utils/sort'

export default {
  inject: ['$helpers'],
  components: { SwFilterableSelect },
  data() {
    return {
      agencies: [],
      residences: [],
      places: [],
      selectedAgency: { label: '', value: '' },
      selectedResidence: { label: '', value: '' },
      selectedPlace: { label: '', value: '' },
      agencySelectOptions: [],
      residenceSelectOptions: [],
      placeSelectOptions: [],
      isLoading: false,
      callbackLocation: null
    }
  },
  async mounted() {
    if ('location' in this.$route.query) {
      this.callbackLocation = JSON.parse(this.$route.query.location)
      this.populateFromCallbackLocation()
    } else {
      await this.loadAgencies()
    }
  },
  watch: {
    agencies() {
      if (
        this.agencies &&
        this.agencies.length === 1 &&
        !this.callbackLocation
      ) {
        this.selectAgency(this.agencies[0])
      }
    },
    residences() {
      if (this.residences.length === 1 && !this.callbackLocation) {
        this.selectResidence(this.residences[0])
      }
    },
    selectedAgencyId(id) {
      if (this.callbackLocation) return
      const agency = this.agencies.find((a) => a.id === id)
      this.selectAgency(agency)
      this.resetSelectedId()
    },
    selectedResidenceId(id) {
      if (this.callbackLocation) return
      const residence = this.residences.find((r) => r.id === id)
      this.selectResidence(residence)
      this.resetSelectedId()
    },
    selectedPlaceId(newId) {
      if (newId) {
        this.$emit('update:filterMode', 'place')
        this.$emit('update:filterValue', newId)
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/attributes'
    }),
    selectedAgencyId() {
      return this.selectedAgency?.value
    },
    selectedResidenceId() {
      return this.selectedResidence?.value
    },
    selectedPlaceId() {
      return this.selectedPlace?.value
    },
    showClearSelectedPlace() {
      return (
        this.selectedPlace.value !== '' && this.placeSelectOptions.length > 1
      )
    }
  },
  methods: {
    sortList(tabOptions) {
      return tabOptions.sort((a, b) => {
        if (a.label === null) {
          return true
        } else {
          return String(a.label).localeCompare(String(b.label), 'fr', {
            ignorePunctuation: true,
            numeric: true,
            sensitivity: 'base'
          })
        }
      })
    },
    selectAgency(agency) {
      this.selectedResidence = { label: '', value: '' }
      if (agency) {
        this.selectedAgency = { label: agency.name, value: agency.id }
        this.$q.loading.show()
        this.residences = []
        this.$SowellProxy
          .loadAgencyResidences(this.selectedAgencyId, true)
          .then((response) => {
            response.data.forEach((residence) => {
              this.residences.push({
                id: residence.id,
                name: residence.name
              })
            })
            this.residenceSelectOptions = this.sortList(
              this.$helpers.resourcesToFormOptions(this.residences)
            )
            this.$q.loading.hide()
            if (this.residences.length === 1) {
              this.selectResidence(this.residences[0])
            }
          })
      }
    },
    selectResidence(residence) {
      this.selectedPlace = { label: '', value: '' }
      if (residence) {
        this.$emit('update:filterMode', 'residence')
        this.$emit('update:filterValue', residence.id)
        this.selectedResidenceId = {
          label: residence.name,
          value: residence.id
        }
        this.$q.loading.show()
        this.places = []
        this.$SowellProxy
          .loadResidencePlaces(this.selectedResidenceId, true)
          .then((response) => {
            response.data.map((place) => {
              this.places.push({
                id: Number(place.id),
                name: place.name
              })
            })
            this.placeSelectOptions = alphabeticalSort(
              this.$helpers.resourcesToFormOptions(this.places),
              (option) => option.label
            )
            this.$q.loading.hide()
            if (this.places.length === 1) {
              this.selectPlace(this.places[0])
            }
          })
      }
    },
    async selectPlace(place) {
      this.selectedPlace = { label: place.name, value: place.id }
      this.$emit('update:filterMode', 'place')
      this.$emit('update:filterValue', place.id)
    },
    getPlaceId() {
      this.$refs['place'].$el.childNodes[2].blur()
    },
    resetSelectedId() {
      this.selectedPlace = { label: '', value: '' }
      this.$emit('update:placeId', '')
      this.$emit('update:filterMode', 'residence')
      this.$emit('update:filterValue', this.selectedResidenceId)
    },
    async loadAgencies() {
      this.isLoading = true
      await this.$SowellProxy
        .loadCompanyAgencies(this.company.id)
        .then((response) => {
          this.agencies = response.data.map((agency) => {
            return {
              id: agency.id,
              name: agency.name
            }
          })
          this.agencySelectOptions = this.sortList(
            this.$helpers.resourcesToFormOptions(this.agencies)
          )
        })
        .finally(() => {
          this.isLoading = false
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async populateFromCallbackLocation() {
      if (this.callbackLocation.type === 'PLACE') {
        await this.$SowellProxy
          .loadAgencyResidenceOfPlace(this.callbackLocation.id)
          .then(async ({ data }) => {
            const agency = data.residence.agency
            const residence = data.residence

            this.agencySelectOptions = this.sortList(
              this.$helpers.resourcesToFormOptions([agency])
            )
            this.selectedAgency = { label: agency.name, value: agency.id }

            this.residenceSelectOptions = this.sortList(
              this.$helpers.resourcesToFormOptions([residence])
            )
            this.selectedResidence = {
              label: data.residence.name,
              value: data.residence.id
            }

            this.placeSelectOptions = this.sortPlaces(
              this.$helpers.resourcesToFormOptions([data])
            )
            this.selectedPlace = { label: data.name, value: data.id }
          })
      }
    }
  }
}
</script>
<style scoped>
.qSelect {
  width: 100%;
}
</style>
